import { useContext } from "react";
import { Routes, Route, Navigate, Outlet } from "react-router-dom";
import Layout from "./components/Layout/Layout";
import { APP_VERSION } from "./constants/constants";
import AuthPage from "./pages/AuthPage";
import AuthContext from "./store/auth-context";
import "bootstrap/dist/css/bootstrap.min.css";
import InvitePage from "./pages/InvitePage";
import DomainDataContextProvider from "./store/domaindata-context";
import TenantConfigContextProvider from "./store/tenantconfig-context";
import TicketContextProvider from "./store/ticket-context";
import storage from "./hooks/storage";
import RecordingPlayer from "./components/CallHistory/RecordingPlayer";
import FileViewer from "./components/CallHistory/FileViewer";

// OAuth imports for Zendesk OAuth flow
import { OAuthClientContextProvider } from "./store/oauthclient-context";
import OAuthGate from "./components/OAuth/OAuthGate";

// Auth gate for /invite* routes. Only guards login; providers live above the
// Routes tree so hopping /invite → /auth → / → /invite (v5 parity redirect
// cascade) does NOT unmount DomainDataContextProvider / TicketContextProvider
// and re-fire their profiles/countries + ticket API calls.
const ProtectedRoute = () => {
  const authCtx = useContext(AuthContext);
  if (!authCtx.isLoggedIn) {
    return <Navigate to="/auth" replace />;
  }
  return (
    <OAuthGate>
      <Outlet />
    </OAuthGate>
  );
};

const AppRoutes = () => {
  const authCtx = useContext(AuthContext);

  const rootRedirect = authCtx.isLoggedIn
    ? "/invite?origin=" + window.origininfo.origin + "&app_guid=" + window.origininfo.app_guid
    : "/auth?origin=" + window.origininfo.origin + "&app_guid=" + window.origininfo.app_guid;

  return (
    <Routes>
      <Route path="/" element={<Navigate to={rootRedirect} replace />} />
      {/* Auth route - only mounted when logged out. Logged-in users hitting
          /auth fall through to the "*" wildcard below, which navigates to "/"
          so rootRedirect re-applies with origin/app_guid preserved. */}
      {!authCtx.isLoggedIn && (
        <Route path="/auth" element={<AuthPage />} />
      )}
      {/* Protected routes - guarded via ProtectedRoute */}
      <Route element={<ProtectedRoute />}>
        <Route path="/invite" element={<InvitePage />} />
        <Route path="/invite/:teamid" element={<InvitePage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};


function App() {
  // Hooks must run on every render, so call useContext before any
  // conditional return below.
  const authCtx = useContext(AuthContext);

  // Modal view: when launched via Zendesk instances.create with a
  // ?view=... parameter, render the standalone modal pane and skip
  // the normal auth + router shell.
  const modalView = new URLSearchParams(window.location.search).get("view");
  if (modalView === "recording_player") {
    return <RecordingPlayer />;
  }
  if (modalView === "file_viewer") {
    return <FileViewer />;
  }

if(storage.get("version")){
  if(APP_VERSION != storage.get("version")){
    storage.clearAll();
    storage.set("version", APP_VERSION)
  }
  else{
    // do nothing
  }

}else{
  storage.set("version", APP_VERSION)
}

  window.origininfo = { origin: "zendesk", app_guid: "" };
  let parts = window.location.href.replace(
    /[?&]+([^=&]+)=([^&]*)/gi,
    function (m, key, value) {
      window.origininfo[key] = decodeURIComponent(value);
    }
  );
  console.warn(window.origininfo);

  // Providers gated on isLoggedIn so they mount once at login and stay put
  // for the whole session — no remount on route changes within /invite*, and
  // no remount on the /auth → / → /invite redirect cascade.
  return (
    <OAuthClientContextProvider>
      <Layout>
        {authCtx.isLoggedIn ? (
          <DomainDataContextProvider>
            <TenantConfigContextProvider>
              <TicketContextProvider>
                <AppRoutes />
              </TicketContextProvider>
            </TenantConfigContextProvider>
          </DomainDataContextProvider>
        ) : (
          <AppRoutes />
        )}
      </Layout>
    </OAuthClientContextProvider>
  );
}

export default App;
